import type { ValidationError } from '@/types/api'

export const REVIEW_RATE_LIMITED = 'review_rate_limited'

export type ModerationFailure =
  | { kind: 'validation'; errors: ValidationError[] }
  | { kind: 'not_allowed' }
  | { kind: 'in_progress' }
  | { kind: 'no_changes' }
  | { kind: 'draft_course' }
  | { kind: 'job_busy'; jobType: string | null }
  | { kind: 'rate_limited'; retryAfterSec: number }
  | { kind: 'unknown' }

const FALLBACK_RETRY_SEC = 60

const rec = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : null
const posNum = (v: unknown): number | null => (typeof v === 'number' && v > 0 ? v : null)

type Rec = Record<string, unknown>

const parse429 = (raw: Rec, detail: Rec | null): ModerationFailure => ({
  kind: 'rate_limited',
  retryAfterSec: posNum(detail?.retry_after) ?? posNum(raw.retryAfterHeader) ?? FALLBACK_RETRY_SEC,
})

const parse409 = (detail: Rec | null): ModerationFailure => {
  if (detail?.code === 'review_in_progress') return { kind: 'in_progress' }
  if (detail?.code === 'draft_course') return { kind: 'draft_course' }
  if (detail?.code !== 'studio_job_in_progress') return { kind: 'unknown' }
  return { kind: 'job_busy', jobType: typeof detail.job_type === 'string' ? detail.job_type : null }
}

/**
 * `err` — то, что бросает `.unwrap()`; `retryAfterHeader` кладёт
 * `transformErrorResponse` ручек проверки. Тело 400 валидатора приходит БЕЗ
 * обёртки `detail`, остальные отказы — с ней.
 */
export const parseModerationError = (err: unknown): ModerationFailure => {
  const raw = rec(err)
  if (!raw) return { kind: 'unknown' }
  const data = rec(raw.data)
  const detail = rec(data?.detail)
  if (raw.status === 400 && data?.isValid === false && Array.isArray(data.errors))
    return { kind: 'validation', errors: data.errors as ValidationError[] }
  if (raw.status === 400 && detail?.code === 'no_changes') return { kind: 'no_changes' }
  if (raw.status === 429) return parse429(raw, detail)
  if (raw.status === 403 && detail?.code === 'publish_not_allowed') return { kind: 'not_allowed' }
  if (raw.status === 409) return parse409(detail)
  return { kind: 'unknown' }
}
